import { ArrowRight, Check, CircleAlert } from "lucide-react";
import type { CSSProperties } from "react";
import { Link } from "react-router-dom";

import {
  deriveHighestVerificationTier,
  formatCompactCurrency,
  type DashboardSnapshot,
  type FinanceReadinessPreviewTier,
} from "../../lib/dashboard";
import { READINESS_FACTOR_LABELS } from "../../lib/readiness/breakdown";
import { FACTOR_TO_PRIMARY_ROW } from "../../lib/readiness/factor-routing";
import {
  buildCapNotice,
  buildFactorRows,
  buildGateRows,
  buildHeroSentence,
  buildScoreMoves,
  buildTimeline,
  isPointsGoal,
  type FactorRowModel,
  type GateRow,
  type ScoreMoves,
  type TimelineMonth,
} from "../../lib/readiness/page-model";
import type {
  ReadinessGoal,
  ReadinessResult,
  ReadinessTarget,
} from "../../lib/readiness-types";
import { cn } from "../../lib/utils";
import { SectionLabel } from "../marketplace/OffersInbox";
import { Skeleton } from "../ui/feedback";
import { PageHeader } from "../ui/page-header";

/**
 * The Readiness page (2026-10-08): the old Overview score card's three tabs
 * (Boost score · Factors · Timeline) as one page. Every figure is read from
 * the readiness `Result` + active target (`GET /api/readiness`) and the
 * dashboard snapshot; `lib/readiness/page-model.ts` selects and words them.
 */
export type ReadinessPageSnapshot = Pick<
  DashboardSnapshot,
  | "financialInputs"
  | "affordabilityBandLow"
  | "affordabilityBandHigh"
  | "buyingPowerMax"
  | "targetPrice"
  | "targetArea"
>;

export interface ReadinessPageViewProps {
  result: ReadinessResult | null;
  target: ReadinessTarget | null;
  isLoading: boolean;
  snapshot: ReadinessPageSnapshot;
}

const VERIFY_HREF = "/dashboard?view=passport#verify";

export function ReadinessPageView({
  result,
  target,
  isLoading,
  snapshot,
}: ReadinessPageViewProps) {
  if (isLoading && !result) {
    return (
      <div className="space-y-8" data-testid="readiness-page-loading">
        <Skeleton className="h-64 w-full" rounded="lg" />
        <Skeleton className="h-72 w-full" rounded="lg" />
      </div>
    );
  }
  if (!result) {
    return (
      <div className="marketplace-surface p-6 text-sm text-muted-foreground" data-testid="readiness-page-empty">
        Your readiness appears once Sill has your financial details.{" "}
        <Link className="font-semibold text-primary" to="/dashboard?view=passport">
          Add them on your Passport
        </Link>
        .
      </div>
    );
  }

  const moves = buildScoreMoves(result);
  const gates = buildGateRows(result, snapshot.targetPrice);
  const capNotice = buildCapNotice(result, snapshot.targetPrice);
  const timeline = buildTimeline(result, target?.timeline);
  const factors = buildFactorRows(result, moves);
  const goals = result.goals ?? [];
  const showMoves = Boolean(moves && moves.steps.length > 0);
  const hasPath =
    showMoves || Boolean(gates) || timeline.hasEvents || result.score === null;

  return (
    <div className="space-y-8" data-testid="readiness-page">
      <PageHeader
        className="px-1 pb-1 pt-2"
        description="How agents see your buying strength, and the fastest way to raise it."
        title="Readiness"
      />
      <ReadinessHero
        gates={gates}
        moves={moves}
        result={result}
        snapshot={snapshot}
      />

      {hasPath ? (
        <section className="space-y-3" data-testid="readiness-path">
          <SectionLabel label="Your path to ready" />
          {/* Each card renders only when it has something real to say: the
              score chart needs a points-moving goal, the timeline a dated
              event. A capped score explains itself instead of the chart. */}
          <div className={cn("grid gap-4", showMoves && gates && "lg:grid-cols-2")}>
            {showMoves && moves ? <ScoreMovesCard moves={moves} /> : null}
            {capNotice ? (
              <div
                className="marketplace-surface flex gap-3 p-5 sm:p-6"
                data-testid="readiness-cap-notice"
              >
                <CircleAlert aria-hidden className="mt-0.5 size-5 shrink-0 text-warning" />
                <div className="space-y-1">
                  <h2 className="text-[15px] font-bold">{capNotice.title}</h2>
                  <p className="text-[15px] leading-relaxed text-foreground/80">{capNotice.body}</p>
                </div>
              </div>
            ) : null}
            {gates ? <GatesCard gates={gates} /> : null}
          </div>
          {timeline.hasEvents ? (
            <TimelineCard months={timeline.months} windowLabel={timeline.windowLabel} />
          ) : null}
        </section>
      ) : null}

      {goals.length > 0 ? (
        <section className="space-y-3" data-testid="readiness-next-moves">
          <SectionLabel label="Next moves" />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {goals.map((goal, i) => (
              <MoveCard
                goal={goal}
                highlighted={i === 0}
                key={`${goal.factor}-${goal.action_label}`}
                number={i + 1}
              />
            ))}
          </div>
        </section>
      ) : null}

      {factors.length > 0 ? (
        <section className="space-y-3" data-testid="readiness-factors">
          <SectionLabel label="What's shaping your score" />
          <div className="marketplace-surface grid gap-x-12 px-5 py-1 sm:px-6 md:grid-cols-2">
            {factors.map((factor) => (
              <FactorRow factor={factor} key={factor.id} />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}

// ── Hero ───────────────────────────────────────────────────────────────

const LADDER: Array<{ tier: FinanceReadinessPreviewTier; label: string }> = [
  { tier: "self-reported", label: "Self" },
  { tier: "document-backed", label: "Document" },
  { tier: "bank-verified", label: "Bank" },
  { tier: "lender-approved", label: "Lender" },
];

function ReadinessHero({
  result,
  snapshot,
  moves,
  gates,
}: {
  result: ReadinessResult;
  snapshot: ReadinessPageSnapshot;
  moves: ScoreMoves | null;
  gates: GateRow[] | null;
}) {
  const highest = deriveHighestVerificationTier(snapshot);
  const highestRank = LADDER.findIndex((rung) => rung.tier === highest);
  const band = result.uncertainty_band;
  const buyingPower =
    snapshot.affordabilityBandLow && snapshot.affordabilityBandHigh
      ? `${formatCompactCurrency(snapshot.affordabilityBandLow)} – ${formatCompactCurrency(snapshot.affordabilityBandHigh)}`
      : snapshot.buyingPowerMax > 0
        ? `Up to ${formatCompactCurrency(snapshot.buyingPowerMax)}`
        : null;

  return (
    <section
      className="grid gap-8 rounded-3xl bg-panel-ink bg-linear-to-br from-panel-ink to-panel-ink-end px-5 py-6 text-panel-ink-foreground sm:px-8 sm:py-8 lg:grid-cols-2"
      data-testid="readiness-hero"
    >
      <div className="flex flex-col gap-4">
        <p className="eyebrow-label text-[11px] font-bold tracking-[0.18em] text-panel-ink-accent">
          {snapshot.targetArea ? `READINESS · ${snapshot.targetArea}` : "READINESS"}
        </p>
        <p className="flex flex-wrap items-baseline gap-3">
          <span className="text-6xl font-bold tabular-nums tracking-[-0.04em]" data-testid="readiness-score">
            {result.score ?? "—"}
          </span>
          <span className="text-xl font-semibold text-panel-ink-foreground/80">{result.label}</span>
        </p>
        <p className="text-base leading-relaxed text-panel-ink-foreground/80" data-testid="readiness-hero-sentence">
          {buildHeroSentence(moves, gates)}
        </p>
        <dl className="mt-auto flex flex-wrap gap-x-8 gap-y-3 pt-2">
          {buyingPower ? (
            <HeroStat label="Buying power" value={buyingPower} />
          ) : null}
          {snapshot.targetPrice > 0 ? (
            <HeroStat label="Target" value={formatCompactCurrency(snapshot.targetPrice)} />
          ) : null}
        </dl>
      </div>

      <div className="flex flex-col gap-4 rounded-2xl bg-panel-ink-foreground/[0.06] p-5 sm:p-6" data-testid="readiness-confidence">
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-panel-ink-foreground/60">
          How sure the score is
        </p>
        {result.score !== null ? (
          <div>
            <div className="relative h-2.5 rounded-full bg-panel-ink-foreground/12">
              <div
                className="absolute inset-y-0 rounded-full bg-panel-ink-accent/45 [left:var(--band-left)] [width:var(--band-width)]"
                style={
                  {
                    "--band-left": `${band.low}%`,
                    "--band-width": `${Math.max(band.high - band.low, 1)}%`,
                  } as CSSProperties
                }
              />
              <div
                className="absolute -top-1 h-[18px] w-1 rounded-sm bg-panel-ink-accent [left:var(--score-left)]"
                style={{ "--score-left": `${result.score}%` } as CSSProperties}
              />
            </div>
            <p className="mt-2 text-xs text-panel-ink-foreground/65">
              Your score could be {band.low}–{band.high}
            </p>
          </div>
        ) : null}
        <p className="text-sm leading-relaxed text-panel-ink-foreground/80">
          Verifying doesn't change the score. It narrows this range, so agents can trust it.
          {result.sharpen_hint ? ` ${result.sharpen_hint.action_label}.` : ""}
        </p>
        <ol className="grid grid-cols-4 gap-2" aria-label="Verification level">
          {LADDER.map((rung, i) => (
            <li
              className={cn(
                "flex flex-col gap-0.5 rounded-xl p-2.5",
                i <= highestRank
                  ? "bg-panel-ink-accent/15"
                  : i === highestRank + 1
                    ? "border border-dashed border-panel-ink-accent/70"
                    : "border border-panel-ink-foreground/15",
              )}
              key={rung.tier}
            >
              <span
                className={cn(
                  "text-xs font-bold",
                  i <= highestRank ? "text-panel-ink-accent" : i === highestRank + 1 ? "" : "text-panel-ink-foreground/60",
                )}
              >
                {rung.label}
              </span>
              <span className="text-[11px] text-panel-ink-foreground/60">
                {i <= highestRank ? "Done" : i === highestRank + 1 ? "Next" : "Later"}
              </span>
            </li>
          ))}
        </ol>
        {highestRank < LADDER.length - 1 ? (
          <Link
            className="inline-flex min-h-12 items-center justify-center rounded-full bg-panel-ink-foreground px-6 text-base font-semibold text-panel-ink transition-colors hover:bg-panel-ink-foreground/90"
            data-testid="readiness-verify-cta"
            to={VERIFY_HREF}
          >
            Verify your numbers
          </Link>
        ) : null}
      </div>
    </section>
  );
}

function HeroStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1">
      <dt className="text-[11px] font-semibold uppercase tracking-[0.14em] text-panel-ink-foreground/60">{label}</dt>
      <dd className="text-xl font-semibold tabular-nums">{value}</dd>
    </div>
  );
}

// ── Path ───────────────────────────────────────────────────────────────

function ScoreMovesCard({ moves }: { moves: ScoreMoves }) {
  // Bar heights span the climb only (start → end), with a floor so the
  // "today" bar never collapses to nothing.
  const range = Math.max(moves.end - moves.start, 1);
  const heightFor = (score: number) => `${40 + ((score - moves.start) / range) * 60}%`;
  const columns = [
    { key: "today", score: moves.start, delta: null as number | null, title: "Today", detail: "Where you are" },
    ...moves.steps.map((step, i) => ({
      key: `${step.goal.factor}-${i}`,
      score: step.scoreAfter,
      delta: step.delta,
      title: `${i + 1} · ${READINESS_FACTOR_LABELS[step.goal.factor]}`,
      detail: step.goal.current_state && step.goal.target_state
        ? `${step.goal.current_state} → ${step.goal.target_state}`
        : step.goal.action_label,
    })),
  ];

  return (
    <div className="marketplace-surface flex flex-col gap-5 p-5 sm:p-6" data-testid="readiness-score-moves">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[15px] font-bold">Score moves</h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {moves.start} → about {moves.end}
        </span>
      </div>
      <div
        className="grid h-48 items-end gap-3 border-b border-border [grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
        style={{ "--cols": columns.length } as CSSProperties}
      >
        {columns.map((col, i) => (
          <div className="flex h-full flex-col items-center justify-end gap-2" key={col.key}>
            {col.delta !== null ? (
              <span className="rounded-full bg-primary-soft px-2.5 py-0.5 text-xs font-bold text-primary tabular-nums">
                ≈ +{col.delta}
              </span>
            ) : null}
            <div
              className={cn(
                "flex w-full max-w-32 justify-center rounded-t-xl pt-2.5 text-lg font-bold tabular-nums [height:var(--bar-h)]",
                i === 0
                  ? "bg-muted text-foreground"
                  : i === columns.length - 1
                    ? "bg-primary text-primary-foreground"
                    : "bg-primary/55 text-primary-foreground",
              )}
              style={{ "--bar-h": heightFor(col.score) } as CSSProperties}
            >
              {col.score}
            </div>
          </div>
        ))}
      </div>
      <div
        className="grid gap-3 text-center [grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
        style={{ "--cols": columns.length } as CSSProperties}
      >
        {columns.map((col) => (
          <div className="flex min-w-0 flex-col gap-0.5" key={col.key}>
            <span className="text-sm font-semibold">{col.title}</span>
            <span className="text-xs text-muted-foreground">{col.detail}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-muted-foreground">
        Each gain is estimated on its own, so the total is approximate.
      </p>
    </div>
  );
}

function GatesCard({ gates }: { gates: GateRow[] }) {
  const metCount = gates.filter((g) => g.met).length;
  return (
    <div className="marketplace-surface flex flex-col gap-2 p-5 sm:p-6" data-testid="readiness-gates">
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="text-[15px] font-bold">Ready to act needs</h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {metCount} of {gates.length}
        </span>
      </div>
      <ul>
        {gates.map((gate, i) => (
          <li
            className={cn(
              "flex items-center gap-3 py-3.5",
              i < gates.length - 1 && "border-b border-border-soft",
            )}
            data-gate={gate.id}
            data-met={gate.met}
            key={gate.id}
          >
            {gate.met ? (
              <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-primary-soft text-primary">
                <Check aria-hidden className="size-4" />
              </span>
            ) : (
              <span className="size-7 shrink-0 rounded-full border-2 border-dashed border-warning" />
            )}
            <span className="flex-1 text-[15px]">
              {gate.label}
              <span className="sr-only">{gate.met ? " — met" : " — not met yet"}</span>
            </span>
            <span className={cn("text-sm", gate.met ? "text-muted-foreground" : "text-warning-foreground")}>
              {gate.detail}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function TimelineCard({
  months,
  windowLabel,
}: {
  months: TimelineMonth[];
  windowLabel: string | null;
}) {
  return (
    <div className="marketplace-surface flex flex-col gap-4 p-5 sm:p-6" data-testid="readiness-timeline">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-[15px] font-bold">Timeline</h2>
        {windowLabel ? <span className="text-sm text-muted-foreground">{windowLabel}</span> : null}
      </div>
      <div className="overflow-x-auto">
        <ol
          className="grid min-w-[36rem] gap-1.5 [grid-template-columns:repeat(var(--cols),minmax(0,1fr))]"
          style={{ "--cols": months.length } as CSSProperties}
        >
          {months.map((month, i) => (
            <li className="flex flex-col gap-2" key={`${month.label}-${i.toString()}`}>
              <div
                className={cn(
                  "h-2 rounded-full",
                  month.isToday ? "bg-panel-ink" : month.inWindow ? "bg-warning" : "bg-muted",
                )}
              />
              <span
                className={cn(
                  "text-xs",
                  month.isToday ? "font-bold" : month.inWindow ? "font-semibold text-warning-foreground" : "text-muted-foreground",
                )}
              >
                {month.isToday ? `${month.label} · today` : month.label}
              </span>
              {month.events.map((event) => (
                <span className="text-xs text-muted-foreground" key={event}>
                  {event}
                </span>
              ))}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}

// ── Next moves ─────────────────────────────────────────────────────────

function MoveCard({
  goal,
  number,
  highlighted,
}: {
  goal: ReadinessGoal;
  number: number;
  highlighted: boolean;
}) {
  const row = FACTOR_TO_PRIMARY_ROW[goal.factor];
  const href = row ? `/dashboard?view=passport#profile-row-${row}` : "/dashboard?view=passport";
  return (
    <article
      className={cn(
        "marketplace-surface flex flex-col gap-3 p-5",
        highlighted && "ring-2 ring-primary",
      )}
      data-goal-kind={goal.kind || "raise"}
      data-testid="readiness-move-card"
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className={cn(
            "text-[11px] font-bold uppercase tracking-[0.14em]",
            highlighted ? "text-primary" : "text-muted-foreground",
          )}
        >
          Move {number} · {READINESS_FACTOR_LABELS[goal.factor]}
        </span>
        {isPointsGoal(goal) ? (
          <span className="text-sm font-bold text-primary tabular-nums">≈ +{goal.projected_delta}</span>
        ) : null}
      </div>
      <h3 className="text-lg font-bold leading-snug">{goal.action_label}</h3>
      {goal.why ? <p className="text-sm leading-relaxed text-muted-foreground">{goal.why}</p> : null}
      <Link
        className={cn(
          "mt-auto inline-flex min-h-11 items-center justify-center gap-1.5 rounded-full px-5 text-sm font-semibold transition-colors",
          highlighted
            ? "bg-primary text-primary-foreground hover:bg-primary/92"
            : "border border-border text-foreground hover:bg-primary/5",
        )}
        to={href}
      >
        Update on Passport
        <ArrowRight aria-hidden className="size-4" />
      </Link>
    </article>
  );
}

// ── Factors ────────────────────────────────────────────────────────────

const STATUS_CHIP: Record<FactorRowModel["status"], string> = {
  move: "bg-warning/20 text-warning-foreground",
  strong: "bg-primary-soft text-primary",
  grow: "bg-muted text-foreground",
  unknown: "bg-muted text-muted-foreground",
};

function FactorRow({ factor }: { factor: FactorRowModel }) {
  const label =
    factor.status === "move"
      ? `Move ${factor.moveNumber ?? ""}`
      : factor.status === "strong"
        ? "Strong"
        : factor.status === "grow"
          ? "Room to grow"
          : "Not provided";
  return (
    <div className="flex flex-col gap-2.5 border-b border-border-soft py-4" data-factor={factor.id}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-[15px] font-semibold">{factor.label}</span>
        <span className={cn("rounded-full px-2.5 py-0.5 text-xs font-bold", STATUS_CHIP[factor.status])}>
          {label}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
          <div
            className={cn(
              "h-2 rounded-full [width:var(--pct)]",
              factor.status === "move" ? "bg-warning" : factor.status === "strong" ? "bg-primary" : "bg-foreground/35",
            )}
            style={{ "--pct": `${factor.isUnknown ? 0 : factor.percent}%` } as CSSProperties}
          />
        </div>
        <span className="min-w-12 text-right text-sm text-muted-foreground tabular-nums">
          {factor.isUnknown ? "—" : `${factor.value} / ${factor.max}`}
        </span>
      </div>
    </div>
  );
}
